import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons'
import { Tabs, Text } from '@mantine/core'
import { createFormContext, zodResolver } from '@mantine/form'
import { AxiosError } from 'axios'
import { useEffect, useState } from 'react'

import {
  useMutationCreateItem,
  useMutationUpdateItem,
  useMutationEnableItem,
  useMutationDisableItem,
} from '@flecto/core/saas'
import { useMutationClassifyInsurable } from '@flecto/core/shared'
import { useTranslation } from '@flecto/translations'
import { type UNSAFE_any } from '@flecto/types'
import { Icon, useNotifications } from '@flecto/ui'
import { isEqual } from '@flecto/ui/lib/utils'
import { useHandleAxiosError } from '@flecto/utils'

import { type CoreItemForm } from '../../types/CoreItemForm'
import { TabInventory } from './TabInventory'
import { TabPricing } from './TabPricing'
import { TabProperties } from './TabProperties'
import { useFormItemSchema } from './useFormItemSchema'

export const [FormProvider, useFormContext, useForm] =
  createFormContext<CoreItemForm>()

export type InsurableStatus =
  | 'pending'
  | 'loading'
  | 'eligible'
  | 'not_eligible'

type FormItemProps = {
  id?: string
  item?: Partial<CoreItemForm>
  onError?: (message: string) => void
  onSuccess?: () => void
  onChangeLoading?: (loading: boolean) => void
  disableSaveButton: (value: boolean) => void
}

export function FormItem({
  item,
  onError,
  onSuccess,
  onChangeLoading,
  disableSaveButton,
  ...props
}: FormItemProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'items' })

  const { showNotification } = useNotifications()
  const { getErrorMessages } = useHandleAxiosError()

  const [createdItem, setCreatedItem] = useState<Partial<CoreItemForm>>()

  const { schema, initialValues } = useFormItemSchema({ item })

  const { mutateAsync: updateItem } = useMutationUpdateItem()
  const { mutateAsync: createItem } = useMutationCreateItem()
  const { mutateAsync: enableItem } = useMutationEnableItem()
  const { mutateAsync: disableItem } = useMutationDisableItem()
  const { mutateAsync: checkIsInsurable } = useMutationClassifyInsurable()

  const initialIsInsurableState =
    item?.insured === true
      ? 'eligible'
      : item?.insured === false
        ? 'not_eligible'
        : 'pending'

  const [isInsurable, setIsInsurable] = useState<InsurableStatus>(
    initialIsInsurableState
  )

  const form = useForm({
    initialValues,
    validate: zodResolver(schema),
  })

  const changes = Object.fromEntries(
    Object.entries(form.values).filter(
      ([key, value]) =>
        !isEqual(value, (initialValues as Record<string, UNSAFE_any>)[key])
    )
  )

  async function saveItem() {
    try {
      onChangeLoading?.(true)

      if (form.values.internal_id) {
        await updateItem({
          itemId: form.values.internal_id,
          item: {
            ...changes,
            allow_overbooking: form.values.allow_overbooking,
            on_store: form.values.on_store,
            quantity: form.values.quantity,
            on_market: form.values.on_market,
            track_quantity: form.values.track_quantity,
            pricing_rules: form.values.pricing_rules,
            pricing_rule_overtime: form.values.pricing_rule_overtime,
            overtime_enabled: form.values.overtime_enabled,
            taxable: form.values.taxable,
            deposit_amount: form.values.deposit_amount,
          },
        })

        if (
          form.values.status !== item?.status &&
          form.values.status !== createdItem?.status
        ) {
          if (form.values.status === 'enabled') {
            await enableItem({ itemId: form.values.internal_id })
          } else {
            await disableItem({ itemId: form.values.internal_id })
          }
        }
      } else {
        const prop =
          form.values.status === 'enabled'
            ? {
                enable: true,
              }
            : {
                disable: true,
              }
        const createdItem = await createItem({
          item: form.values,
          options: prop,
        })

        form.setValues({
          internal_id: createdItem.internal_id,
          status: createdItem.status,
          meta: createdItem.meta,
        })
        setCreatedItem({
          internal_id: createdItem.internal_id,
          status: createdItem.status,
          meta: createdItem.meta,
        })
      }

      showNotification({
        type: 'success',
        message: t('notification_save_item_success'),
      })
      onSuccess?.()
    } catch (e) {
      showNotification({
        type: 'error',
        message: t('notification_save_item_error'),
      })

      if (e instanceof Error) {
        onError?.(e.message)
      }
      if (e instanceof AxiosError) {
        const errors = getErrorMessages(e, {
          'item[photos]': 'photos_attributes',
          'item[pricing_rules]': 'pricing_rules_unique',
          'item[category]': 'category_id',
        })
        errors.forEach((e) => {
          form.setFieldError(e.field, e.errorMessage)
        })
      }
    } finally {
      onChangeLoading?.(false)
    }
  }

  async function fetchInsurability() {
    if (Object.keys(form.errors ?? {}).length > 0) return

    try {
      disableSaveButton?.(true)
      setIsInsurable('loading')

      const isItemInsurable = await checkIsInsurable({
        itemName: `${form.values.title}`,
        categoryId: form.values.category_id,
      })

      setIsInsurable(isItemInsurable.insured ? 'eligible' : 'not_eligible')
    } catch (error) {
      setIsInsurable('not_eligible')

      if (error instanceof Error) {
        onError?.(error.message)
      }
    } finally {
      disableSaveButton?.(false)
    }
  }

  useEffect(() => {
    if (item) {
      form.setValues(initialValues)
    } else {
      form.reset()
    }
  }, [item])

  const hasPricingRulesErrors = (): boolean => {
    const terms = ['pricing_rules', 'pricing_rule_overtime']

    return Object.entries(form.errors).some(([key]) =>
      terms.some((term) => key.startsWith(term))
    )
  }

  const hasErrorsTabProperties = Boolean(
    form.errors &&
      (form.errors.title ||
        form.errors.description ||
        form.errors.category_id ||
        form.errors.owner_address_id ||
        form.errors.photos_attributes)
  )

  const hasErrorsTabPricing = Boolean(
    form.errors &&
      (form.errors.taxable ||
        form.errors.tax_rate ||
        form.errors.deposit_amount ||
        hasPricingRulesErrors())
  )

  const hasErrorsTabInventory = Boolean(
    form.errors &&
      (form.errors.allow_overbooking ||
        form.errors.track_quantity ||
        form.errors.quantity ||
        form.errors.quantity ||
        form.errors.on_store)
  )

  const tabs = [
    {
      id: 'properties',
      title: t('label_properties'),
      component: (
        <TabProperties
          isInsurable={isInsurable}
          changeInsurability={setIsInsurable}
          fetchInsurability={fetchInsurability}
        />
      ),
      hasErrors: hasErrorsTabProperties,
    },
    {
      id: 'pricing',
      title: t('label_pricing'),
      component: <TabPricing />,
      hasErrors: hasErrorsTabPricing,
    },
    {
      id: 'inventory',
      title: t('label_inventory'),
      component: <TabInventory />,
      hasErrors: hasErrorsTabInventory,
    },
  ]

  return (
    <FormProvider form={form}>
      <form
        {...props}
        onSubmit={async (e) => {
          e.preventDefault()

          form.validate()

          await saveItem()
        }}
      >
        <Tabs defaultValue='properties'>
          <Tabs.List>
            {tabs.map((tab) => (
              <Tabs.Tab
                key={tab.id}
                value={tab.id}
                color={tab.hasErrors ? 'red' : undefined}
                data-testid={`tab-${tab.id}`}
                leftSection={
                  tab.hasErrors ? (
                    <Icon icon={faExclamationCircle} color='red' />
                  ) : undefined
                }
              >
                <Text color={tab.hasErrors ? 'red' : undefined}>
                  {tab.title}
                </Text>
              </Tabs.Tab>
            ))}
          </Tabs.List>

          {tabs.map((tab) => (
            <Tabs.Panel pt='lg' value={tab.id} key={tab.id}>
              {tab.component}
            </Tabs.Panel>
          ))}
        </Tabs>
      </form>
    </FormProvider>
  )
}
